import { useEffect, useRef, useState } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export interface AuthState {
  user: User | null;
  session: Session | null;
  orgId: string | null;
  isPlatformOwner: boolean;
  subscriptionStatus: string | null;
  loading: boolean;
  error: string | null;
}

export const useProvideAuth = () => {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    orgId: null,
    isPlatformOwner: false,
    subscriptionStatus: null,
    loading: true,
    error: null,
  });
  const authRequestRef = useRef(0);

  useEffect(() => {
    let isMounted = true;
    let unsubscribe: (() => void) | undefined;
    let hasResolvedInitialSession = false;

    const applySignedOutState = () => {
      if (!isMounted) return;

      setState({
        user: null,
        session: null,
        orgId: null,
        isPlatformOwner: false,
        subscriptionStatus: null,
        loading: false,
        error: null,
      });
    };

    const fetchOrgData = async (userId: string) => {
      const requestId = ++authRequestRef.current;

      try {
        const [profileResult, roleResult] = await Promise.all([
          supabase
            .from("profiles")
            .select("org_id")
            .eq("id", userId)
            .maybeSingle(),
          supabase
            .from("user_roles")
            .select("role")
            .eq("user_id", userId)
            .eq("role", "owner")
            .is("org_id", null)
            .maybeSingle(),
        ]);

        if (profileResult.error && profileResult.error.code !== "PGRST116") {
          throw profileResult.error;
        }

        if (roleResult.error && roleResult.error.code !== "PGRST116") {
          throw roleResult.error;
        }

        let subscriptionStatus: string | null = null;

        if (profileResult.data?.org_id) {
          const { data: org, error: orgError } = await supabase
            .from("organizations")
            .select("subscription_status")
            .eq("id", profileResult.data.org_id)
            .maybeSingle();

          if (orgError && orgError.code !== "PGRST116") {
            throw orgError;
          }

          subscriptionStatus = org?.subscription_status ?? null;
        }

        if (!isMounted || authRequestRef.current !== requestId) return;

        setState((prev) => ({
          ...prev,
          orgId: profileResult.data?.org_id ?? null,
          isPlatformOwner: Boolean(roleResult.data),
          subscriptionStatus,
          loading: false,
          error: null,
        }));
      } catch (error: any) {
        console.error("Org data fetch error:", error);

        if (!isMounted || authRequestRef.current !== requestId) return;

        setState((prev) => ({
          ...prev,
          orgId: null,
          isPlatformOwner: false,
          subscriptionStatus: null,
          loading: false,
          error: error?.message ?? "Failed to load authentication state",
        }));
      }
    };

    const hydrateFromSession = async (session: Session | null) => {
      if (!isMounted) return;

      authRequestRef.current += 1;

      setState({
        session,
        user: session?.user ?? null,
        orgId: null,
        isPlatformOwner: false,
        subscriptionStatus: null,
        loading: Boolean(session?.user),
        error: null,
      });

      if (session?.user) {
        await fetchOrgData(session.user.id);
      } else {
        applySignedOutState();
      }

      hasResolvedInitialSession = true;
    };

    const initializeAuth = async () => {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (error) {
        console.error("Session restore error:", error);
        applySignedOutState();
      } else if (session) {
        // Verify the session is still valid server-side
        const { error: userError } = await supabase.auth.getUser(session.access_token);
        if (userError) {
          console.warn("Stale session detected, clearing:", userError.message);
          await supabase.auth.signOut().catch(() => {});
          applySignedOutState();
        } else {
          await hydrateFromSession(session);
        }
      } else {
        applySignedOutState();
      }

      hasResolvedInitialSession = true;

      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((event, nextSession) => {
        if (!isMounted) return;

        if (event === "INITIAL_SESSION" && hasResolvedInitialSession) {
          return;
        }

        authRequestRef.current += 1;

        setState({
          session: nextSession,
          user: nextSession?.user ?? null,
          orgId: null,
          isPlatformOwner: false,
          subscriptionStatus: null,
          loading: Boolean(nextSession?.user),
          error: null,
        });

        if (nextSession?.user) {
          window.setTimeout(() => {
            void fetchOrgData(nextSession.user.id);
          }, 0);
        } else {
          applySignedOutState();
        }
      });

      unsubscribe = () => subscription.unsubscribe();
    };

    void initializeAuth();

    return () => {
      isMounted = false;
      unsubscribe?.();
    };
  }, []);

  return state;
};
